import {
  collection,
  DocumentData,
  getDocs,
  limit as limitQuery,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { TEST_CHAT_PHONE_NUMBER } from "../../config/config";
import { firestore } from "../../firebase-config";
import type { TestChatReplayRunModel } from "../api/ChatApi";
import type { SupportTicketStatus, SupportTicketSummary } from "../api/ChatApi";
import { chatManager } from "../managers/ChatManager";
import { AgentEnum } from "../models/Agents/AgentEnum";
import { ChatModel } from "../models/Chat/ChatModel";
import { MessageModel } from "../models/Chat/MessageModel";

export interface TestChatSessionModel {
  id: string;
  phoneNumber: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessage: string;
  lastMessageRole?: string;
  testMetadata: TestChatMetadataModel;
}

export interface TestChatLabelModel {
  id: string;
  name: string;
  color: string;
}

export interface TestChatEvaluationModel {
  rating: number;
  title: string;
  description: string;
  finalizedAt?: Date | null;
}

export interface TestChatMetadataModel {
  notes: string;
  labels: TestChatLabelModel[];
  evaluation: TestChatEvaluationModel;
  updatedAt?: Date | null;
}

export interface FirestoreTestChatReplayRunModel
  extends Omit<TestChatReplayRunModel, "createdAt" | "completedAt"> {
  createdAt: Date;
  completedAt: Date;
  updatedAt?: Date | null;
}

const toMessageModel = (data: any, id?: string) =>
  new MessageModel({
    ...data,
    id: data.id || id,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    sourceCreatedAt: toOptionalDate(data.sourceCreatedAt),
  });

const toOptionalDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const toTimeValue = (value: any) => {
  const date = toOptionalDate(value);
  return date ? date.getTime() : 0;
};

const normalizeTestChatMetadata = (data?: any): TestChatMetadataModel => ({
  notes: data?.notes || "",
  labels: Array.isArray(data?.labels)
    ? data.labels
        .filter((label: any) => label?.name?.trim())
        .map((label: any) => ({
          id: label.id || label.name.trim().toLowerCase().replace(/\s+/g, "-"),
          name: label.name.trim(),
          color: label.color || "#1f6feb",
        }))
    : [],
  evaluation: {
    rating:
      typeof data?.evaluation?.rating === "number"
        ? Math.max(0, Math.min(10, Math.round(data.evaluation.rating)))
        : 0,
    title: data?.evaluation?.title || "",
    description: data?.evaluation?.description || "",
    finalizedAt: toOptionalDate(data?.evaluation?.finalizedAt),
  },
  updatedAt: toOptionalDate(data?.updatedAt),
});

const normalizeTestChatReplayRun = (
  id: string,
  data: any
): FirestoreTestChatReplayRunModel => ({
  id,
  sourceServiceCode: data?.sourceServiceCode || "",
  replayServiceCode: data?.replayServiceCode || "",
  replayPhoneNumber: data?.replayPhoneNumber || "",
  createdAt: toOptionalDate(data?.createdAt) || new Date(),
  completedAt: toOptionalDate(data?.completedAt) || new Date(),
  updatedAt: toOptionalDate(data?.updatedAt),
  status: data?.status === "failed" ? "failed" : "completed",
  sourceUserTurns: Number(data?.sourceUserTurns || 0),
  replayUserTurns: Number(data?.replayUserTurns || 0),
  exactToolMatch: data?.exactToolMatch === true,
  exactTurnToolMatch: data?.exactTurnToolMatch === true,
  baselineToolSequence: Array.isArray(data?.baselineToolSequence)
    ? data.baselineToolSequence.filter(Boolean)
    : [],
  replayToolSequence: Array.isArray(data?.replayToolSequence)
    ? data.replayToolSequence.filter(Boolean)
    : [],
  missingTools: Array.isArray(data?.missingTools)
    ? data.missingTools.filter(Boolean)
    : [],
  unexpectedTools: Array.isArray(data?.unexpectedTools)
    ? data.unexpectedTools.filter(Boolean)
    : [],
  turns: Array.isArray(data?.turns)
    ? data.turns.map((turn: any) => ({
        turnIndex: Number(turn?.turnIndex || 0),
        userMessage: turn?.userMessage || "",
        messageType: turn?.messageType || "TEXT",
        baselineToolNames: Array.isArray(turn?.baselineToolNames)
          ? turn.baselineToolNames.filter(Boolean)
          : [],
        replayToolNames: Array.isArray(turn?.replayToolNames)
          ? turn.replayToolNames.filter(Boolean)
          : [],
        missingTools: Array.isArray(turn?.missingTools)
          ? turn.missingTools.filter(Boolean)
          : [],
        unexpectedTools: Array.isArray(turn?.unexpectedTools)
          ? turn.unexpectedTools.filter(Boolean)
          : [],
        exactToolMatch: turn?.exactToolMatch === true,
        baselineAssistantResponse: turn?.baselineAssistantResponse || "",
        replayAssistantResponse: turn?.replayAssistantResponse || "",
      }))
    : [],
  sourceFinalState: data?.sourceFinalState || null,
  replayFinalState: data?.replayFinalState || null,
  stateDifferences: Array.isArray(data?.stateDifferences)
    ? data.stateDifferences.filter(Boolean)
    : [],
  baselineLastAssistantResponse: data?.baselineLastAssistantResponse || "",
  replayLastAssistantResponse: data?.replayLastAssistantResponse || "",
  error: data?.error || "",
});

const filterVisibleMessages = (messages: MessageModel[]) => {
  const agentNames = Object.values(AgentEnum);
  return messages.filter(
    (message) =>
      message.role !== "system" && !agentNames.includes(message.name as AgentEnum)
  );
};

const companyUserChatsCollection = (company: string) =>
  query(collection(firestore, "userChats"), where("companyId", "==", company));

const companyChatsCollection = (company: string) =>
  query(collection(firestore, "chats"), where("companyId", "==", company));

const legacyCompanyUserChatsCollection = (company: string) =>
  collection(firestore, `users-${company}`);

const legacyCompanyChatsCollection = (company: string) =>
  collection(firestore, `chats-${company}`);

const messagesCollection = () => collection(firestore, "messages");

const legacyMessagesCollection = (company: string) =>
  collection(firestore, `ws-${company}`);

const chatTestRunsCollection = () => collection(firestore, "chatTestRuns");

/**
 * Support queue through the authorized API. We poll while the panel is open so
 * ticket data never depends on permissive browser Firestore rules.
 */
export const useSupportTickets = (
  company: string,
  status: SupportTicketStatus,
  enabled: boolean
) => {
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [statusCounts, setStatusCounts] = useState<{ OPEN: number; IN_PROGRESS: number }>({ OPEN: 0, IN_PROGRESS: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company || !enabled) {
      setTickets([]);
      setNextCursor(undefined);
      setStatusCounts({ OPEN: 0, IN_PROGRESS: 0 });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    let active = true;
    const load = async () => {
      try {
        const result = await chatManager.getSupportTickets({ status, limit: 100 });
        if (active) {
          setTickets(result.tickets || []);
          setNextCursor(result.nextCursor);
          setStatusCounts({
            OPEN: result.statusCounts?.OPEN || 0,
            IN_PROGRESS: result.statusCounts?.IN_PROGRESS || 0,
          });
          setError(null);
        }
      } catch (loadError: any) {
        if (active) {
          setError(loadError?.message || "No se pudieron cargar los tickets de soporte.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    const refresh = window.setInterval(() => void load(), 10000);
    return () => {
      active = false;
      window.clearInterval(refresh);
    };
  }, [company, enabled, status]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const result = await chatManager.getSupportTickets({
        status,
        before: nextCursor,
        limit: 100,
      });
      setTickets((current) => {
        const existing = new Set(current.map((ticket) => ticket.id));
        return [...current, ...(result.tickets || []).filter((ticket) => !existing.has(ticket.id))];
      });
      setNextCursor(result.nextCursor);
    } catch (loadError: any) {
      setError(loadError?.message || "No se pudieron cargar más tickets de soporte.");
    } finally {
      setLoadingMore(false);
    }
  };

  return { tickets, loading, loadingMore, hasMore: Boolean(nextCursor), loadMore, error, statusCounts };
};

export const useAllChats = (company: string, limit?: number) => {
  const [chats, setChats] = useState<ChatModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedInitialSnapshotRef = useRef(false);

  useEffect(() => {
    if (!company) return;
    hasLoadedInitialSnapshotRef.current = false;
    const sound = new Audio("/assets/notification.mp3");
    let unsubscribe = () => {};
    let fallbackUnsubscribe = () => {};

    const attachFallback = () => {
      const legacyQuery = query(
        legacyCompanyUserChatsCollection(company)
      );
      fallbackUnsubscribe = onSnapshot(
        legacyQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          const chatData = snapshot.docs
            .filter((doc) => doc.id !== TEST_CHAT_PHONE_NUMBER)
            .filter((doc) => doc.data()?.isTestReplay !== true)
            .map((doc) => new ChatModel(doc.data(), doc.id))
            .sort(
              (left, right) =>
                toTimeValue(right.updatedAt || right.lastMessageDate || right.createdAt) -
                toTimeValue(left.updatedAt || left.lastMessageDate || left.createdAt)
            );
          setChats(chatData);
          hasLoadedInitialSnapshotRef.current = true;
          setLoading(false);
        },
        (err) => {
          console.log("Error al obtener los chats legacy:", err);
          setError("Error al obtener los chats: " + err.message);
          setLoading(false);
        }
      );
      unsubscribe = fallbackUnsubscribe;
    };

    const primaryQuery = query(
      companyUserChatsCollection(company)
    );
    unsubscribe = onSnapshot(
      primaryQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        if (snapshot.empty) {
          unsubscribe();
          attachFallback();
          return;
        }

        const chatData = snapshot.docs
          .filter((doc) => doc.id !== TEST_CHAT_PHONE_NUMBER)
          .filter((doc) => doc.data()?.isTestReplay !== true)
          .map((doc) => new ChatModel(doc.data(), doc.id))
          .sort(
            (left, right) =>
              toTimeValue(right.updatedAt || right.lastMessageDate || right.createdAt) -
              toTimeValue(left.updatedAt || left.lastMessageDate || left.createdAt)
          );
        setChats(chatData);
        const hasVisibleChanges = snapshot.docChanges().some((change) => {
          if (change.doc.id === TEST_CHAT_PHONE_NUMBER) return false;
          if (change.type === "removed") return false;
          const data = change.doc.data();
          return data?.lastMessageRole === "user";
        });
        if (hasLoadedInitialSnapshotRef.current && hasVisibleChanges) {
          sound.play().catch((e) => console.warn("Error reproduciendo sonido:", e));
        }
        hasLoadedInitialSnapshotRef.current = true;
        setLoading(false);
      },
      (err) => {
        console.log("Error al obtener los chats:", err);
        setError("Error al obtener los chats: " + err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      fallbackUnsubscribe();
    };
  }, [company, limit]);

  const updateBotStatus = async (phoneNumber: string, enabled: boolean) => {
    try {
      await chatManager.updateChat(phoneNumber);
    } catch (err: any) {
      setError("Error actualizando botIsEnabled: " + err.message);
    }
  };

  return { chats, loading, error, updateBotStatus };
};

export const useChatHistory = (company: string, enabled: boolean, pageSize = 30) => {
  const [chats, setChats] = useState<ChatModel[]>([]);
  const [cursor, setCursor] = useState<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = async (reset: boolean) => {
    if (!company || !enabled || loading) return;
    setLoading(true);
    setError(null);
    try {
      const constraints: any[] = [
        orderBy("updatedAt", "desc"),
        limitQuery(pageSize),
      ];
      if (!reset && cursor) constraints.splice(2, 0, startAfter(cursor));
      const snapshot = await getDocs(query(companyUserChatsCollection(company), ...constraints));
      const page = snapshot.docs
        .filter((doc) => doc.id !== TEST_CHAT_PHONE_NUMBER)
        .filter((doc) => doc.data()?.isTestReplay !== true)
        .map((doc) => new ChatModel(doc.data(), doc.id));
      setChats((current) => (reset ? page : [...current, ...page]));
      setCursor(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === pageSize);
    } catch (error) {
      setError(error instanceof Error ? error.message : "No se pudo cargar el historial.");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setChats([]);
      setCursor(null);
      setHasMore(false);
      return;
    }
    void loadPage(true);
    // La paginación se dispara explícitamente con loadMore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, enabled, pageSize]);

  return {
    chats,
    loading,
    error,
    hasMore,
    loadMore: () => loadPage(false),
  };
};

export const searchCompanyChatsByPhone = async (company: string, phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (!company || digits.length < 4) return [];
  const snapshot = await getDocs(companyUserChatsCollection(company));
  return snapshot.docs
    .filter((doc) => doc.id !== TEST_CHAT_PHONE_NUMBER)
    .filter((doc) => doc.data()?.isTestReplay !== true)
    .filter((doc) => String(doc.data()?.phoneNumber || doc.id).replace(/\D/g, "").includes(digits))
    .map((doc) => new ChatModel(doc.data(), doc.id));
};

export const useChatDocument = (company: string, phoneNumber: string) => {
  const [chat, setChat] = useState<ChatModel | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company || !phoneNumber) return;

    let unsubscribe = () => {};
    let fallbackUnsubscribe = () => {};

    const attachFallback = () => {
      const legacyQ = query(
        legacyCompanyUserChatsCollection(company),
        limitQuery(1)
      );
      fallbackUnsubscribe = onSnapshot(
        legacyQ,
        (snapshot) => {
          if (snapshot.empty) {
            setChat(undefined);
          } else {
            const docSnap = snapshot.docs[0];
            setChat(new ChatModel(docSnap.data(), docSnap.id));
          }
          setLoading(false);
        },
        (err) => {
          setError("Error al obtener el chat: " + err.message);
          setLoading(false);
        }
      );
      unsubscribe = fallbackUnsubscribe;
    };

    const q = query(companyUserChatsCollection(company), limitQuery(1000));
    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docSnap = snapshot.docs.find(
          (doc) => doc.data()?.phoneNumber === phoneNumber
        );
        if (!docSnap) {
          unsubscribe();
          attachFallback();
          return;
        }
        setChat(new ChatModel(docSnap.data(), docSnap.id));
        setLoading(false);
      },
      (err) => {
        setError("Error al obtener el chat: " + err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      fallbackUnsubscribe();
    };
  }, [company, phoneNumber]);

  return { chat, loading, error };
};

export const useServiceMessages = (company: string, serviceCode?: string) => {
  const [messages, setMessages] = useState<MessageModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company || !serviceCode) {
      setMessages([]);
      setError(null);
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);
    let unsubscribe = () => {};
    let fallbackUnsubscribe = () => {};

    const attachFallback = () => {
      const legacyQ = query(
        legacyMessagesCollection(company),
        where("serviceCode", "==", serviceCode)
      );
      fallbackUnsubscribe = onSnapshot(
        legacyQ,
        (snapshot) => {
          const parsedMessages = filterVisibleMessages(
            snapshot.docs.map((messageDoc) =>
              toMessageModel(messageDoc.data(), messageDoc.id)
            )
          );
          setMessages(parsedMessages);
          setLoading(false);
        },
        (err) => {
          setError("Error al obtener los mensajes: " + err.message);
          setLoading(false);
        }
      );
      unsubscribe = fallbackUnsubscribe;
    };

    const q = query(
      messagesCollection(),
      where("companyId", "==", company)
    );

    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const filteredDocs = snapshot.docs.filter(
          (doc) => doc.data()?.serviceCode === serviceCode
        );
        if (!filteredDocs.length) {
          unsubscribe();
          attachFallback();
          return;
        }
        const parsedMessages = filterVisibleMessages(
          filteredDocs
            .sort((left, right) => toTimeValue(left.data()?.createdAt) - toTimeValue(right.data()?.createdAt))
            .map((messageDoc) => toMessageModel(messageDoc.data(), messageDoc.id))
        );
        setMessages(parsedMessages);
        setLoading(false);
      },
      (err) => {
        setError("Error al obtener los mensajes: " + err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      fallbackUnsubscribe();
    };
  }, [company, serviceCode]);

  return { messages, loading, error };
};

export const useTestChatSessions = (company: string, limit?: number) => {
  const [sessions, setSessions] = useState<TestChatSessionModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company) return;

    let unsubscribe = () => {};
    let fallbackUnsubscribe = () => {};

    const attachFallback = () => {
      const legacyQ = query(
        legacyCompanyChatsCollection(company),
        limitQuery(limit || 20)
      );
      fallbackUnsubscribe = onSnapshot(
        legacyQ,
        async (snapshot: QuerySnapshot<DocumentData>) => {
          const sessionList = await Promise.all(
            snapshot.docs.map(async (chatDoc) => {
              const data = chatDoc.data();

              return {
                id: data.serviceCode || chatDoc.id,
                phoneNumber: data.phoneNumber,
                createdAt: data.createdAt?.toDate?.() || new Date(),
                updatedAt:
                  data.updatedAt?.toDate?.() ||
                  data.createdAt?.toDate?.() ||
                  new Date(),
                lastMessage: data.lastMessage || "Nuevo chat de pruebas",
                lastMessageRole: data.lastMessageRole,
                testMetadata: normalizeTestChatMetadata(data.testMetadata),
              } as TestChatSessionModel;
            })
          );

          setSessions(sessionList);
          setLoading(false);
        },
        (err) => {
          setError("Error al obtener el historial de pruebas: " + err.message);
          setLoading(false);
        }
      );
      unsubscribe = fallbackUnsubscribe;
    };

    const chatsRef = companyChatsCollection(company);
    const q = query(chatsRef, limitQuery(limit || 20));

    unsubscribe = onSnapshot(
      q,
      async (snapshot: QuerySnapshot<DocumentData>) => {
        const filteredDocs = snapshot.docs.filter(
          (chatDoc) => chatDoc.data()?.phoneNumber === TEST_CHAT_PHONE_NUMBER
        );
        if (!filteredDocs.length) {
          unsubscribe();
          attachFallback();
          return;
        }

        const sessionList = await Promise.all(
          filteredDocs.map(async (chatDoc) => {
            const data = chatDoc.data();

            return {
              id: data.serviceCode || chatDoc.id,
              phoneNumber: data.phoneNumber,
              createdAt: data.createdAt?.toDate?.() || new Date(),
              updatedAt:
                data.updatedAt?.toDate?.() ||
                data.createdAt?.toDate?.() ||
                new Date(),
              lastMessage: data.lastMessage || "Nuevo chat de pruebas",
              lastMessageRole: data.lastMessageRole,
              testMetadata: normalizeTestChatMetadata(data.testMetadata),
            } as TestChatSessionModel;
          })
        );

        setSessions(sessionList);
        setLoading(false);
      },
      (err) => {
        setError("Error al obtener el historial de pruebas: " + err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      fallbackUnsubscribe();
    };
  }, [company, limit]);

  return { sessions, loading, error };
};

export const useTestChatReplayRuns = (
  company: string,
  serviceCode?: string,
  limit?: number
) => {
  const [runs, setRuns] = useState<FirestoreTestChatReplayRunModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company || !serviceCode) {
      setRuns([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const runsRef = chatTestRunsCollection();
    const q = query(runsRef, where("companyId", "==", company), limitQuery(limit || 12));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const filteredRuns = snapshot.docs
          .filter((runDoc) => runDoc.data()?.sourceServiceCode === serviceCode)
          .sort((left, right) => toTimeValue(right.data()?.createdAt) - toTimeValue(left.data()?.createdAt));
        setRuns(
          filteredRuns.map((runDoc) =>
            normalizeTestChatReplayRun(runDoc.id, runDoc.data())
          )
        );
        setLoading(false);
      },
      (err) => {
        setError("Error al obtener los replays de prueba: " + err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [company, limit, serviceCode]);

  return { runs, loading, error };
};

export const useFirestoreMessages = (
  company: string,
  phoneNumber: string,
  activeServiceId?: string,
  limit?: number
) => {
  const [messages, setMessages] = useState<MessageModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void activeServiceId;
    if (!company || !phoneNumber) {
      setMessages([]);
      setLoading(false);
      setError(null);
      return;
    }
    setMessages([]);
    setLoading(true);
    setError(null);

    // El listener anterior descargaba todos los mensajes de la compañía y
    // filtraba el teléfono en el navegador. En chats grandes hacía que cada
    // selección tardara demasiado. El teléfono reduce la lectura al chat
    // seleccionado; la compañía se valida después por si el número existe en
    // más de una compañía.
    const q = query(
      messagesCollection(),
      where("phoneNumber", "==", phoneNumber),
      ...(limit ? [limitQuery(limit)] : [])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const parsedMessages = filterVisibleMessages(
          snapshot.docs
            .filter((doc) => doc.data()?.companyId === company)
            .filter((doc) => doc.data()?.phoneNumber === phoneNumber)
            .sort((left, right) => toTimeValue(left.data()?.createdAt) - toTimeValue(right.data()?.createdAt))
            .map((messageDoc) => toMessageModel(messageDoc.data(), messageDoc.id))
        );
        setMessages(parsedMessages);
        setLoading(false);
      },
      (err) => {
        console.log("Error al obtener los mensajes:", err);
        setError("Error al obtener los mensajes: " + err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeServiceId, company, phoneNumber, limit]);

  return { messages, loading, error };
};
